import React from 'react';
import Modal from '../../BaseComponents/Modal';
import { ParsedBiomarker } from '../../../api/corevitals';
import ExtractedDataTable from './ExtractedDataTable';
import Button from '../../BaseComponents/Button';
import Select, { SelectOption } from '../../BaseComponents/Select';
import { getAppointmentsList } from '../../../api/appointments';
import { addTestResults } from '../../../api/testResults';
import { getBiomarkersList } from '../../../api/biomarkers';
import { AppointmentLoadStrategy, LocationType } from '../../../types/apiGeneratedTypes';
import './BiomarkerExtractionModal.scss';

interface BiomarkerExtractionModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberName?: string;
  userId?: string;
  parsedBiomarkers: ParsedBiomarker[];
  summary?: Record<string, any>;
  responseData?: Record<string, any>;
  missingList?: Array<{ biomarker_name: string; standard_unit: string }>;
  onSaveSuccess?: () => void;
}

const BiomarkerExtractionModal: React.FC<BiomarkerExtractionModalProps> = ({
  isOpen,
  onClose,
  memberName,
  userId,
  parsedBiomarkers,
  summary,
  responseData,
  missingList,
  onSaveSuccess
}) => {
  const [updatedBiomarkers, setUpdatedBiomarkers] = React.useState<any[]>(parsedBiomarkers);
  const [historicalAppointments, setHistoricalAppointments] = React.useState<SelectOption[]>([]);
  const [selectedAppointment, setSelectedAppointment] = React.useState<SelectOption | null>(null);
  const [isLoadingAppointments, setIsLoadingAppointments] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [appointmentError, setAppointmentError] = React.useState<string | null>(null);
  const [selectedExtractionDate, setSelectedExtractionDate] = React.useState<string | null>(null);
  const [infoMessage, setInfoMessage] = React.useState<string | null>(null);
  const [biomarkersList, setBiomarkersList] = React.useState<any[]>([]);

  const extractionDateBuckets = React.useMemo(() => {
    const bucketsRaw = (responseData as any)?.matched_biomarkers_by_date;
    if (!Array.isArray(bucketsRaw)) return [];

    return bucketsRaw
      .map((bucket: any) => {
        const date = bucket?.['Date'] ?? bucket?.date;
        const biomarkers = bucket?.['Matched Biomarkers'] ?? bucket?.biomarkers ?? [];
        return { date, biomarkers };
      })
      .filter((b) => !!b.date);
  }, [responseData]);

  const showMultiDatePicker = extractionDateBuckets.length > 1;

  const dateOptions = React.useMemo(() => {
    return extractionDateBuckets.map((b) => ({ key: b.date, value: b.date }));
  }, [extractionDateBuckets]);

  React.useEffect(() => {
    if (!isOpen) return;

    // If the API returned multiple date buckets, default to the latest non-empty date.
    if (showMultiDatePicker) {
      const nonEmptyBuckets = extractionDateBuckets.filter(
        (b) => Array.isArray(b.biomarkers) && b.biomarkers.length > 0
      );
      const bucketsToUse =
        nonEmptyBuckets.length > 0 ? nonEmptyBuckets : extractionDateBuckets;
      const latestBucket = bucketsToUse[bucketsToUse.length - 1];

      setSelectedExtractionDate(latestBucket?.date ?? null);
      setUpdatedBiomarkers(latestBucket?.biomarkers ?? []);
    } else {
      setSelectedExtractionDate(null);
      setUpdatedBiomarkers(parsedBiomarkers);
    }

    // Reset appointment/messages whenever we load a new extraction payload.
    setSelectedAppointment(null);
    setSaveError(null);
    setAppointmentError(null);
    setInfoMessage(null);
  }, [isOpen, showMultiDatePicker, extractionDateBuckets, parsedBiomarkers]);

  const fetchBiomarkersList = React.useCallback(async () => {
    try {
      const response = await getBiomarkersList();
      setBiomarkersList(response.biomarkers || []);
    } catch (error) {
      console.error('Error fetching biomarkers list:', error);
    }
  }, []);

  const fetchHistoricalAppointments = React.useCallback(async () => {
    if (!userId) return;
    
    setIsLoadingAppointments(true);
    try {
      // Fetch all appointments for the user
      const appointmentsResponse = await getAppointmentsList(
        1000, // Large limit to get all appointments
        0,
        userId,
        AppointmentLoadStrategy.ALL_DATA
      );

      // Filter appointments with location type HISTORIC_RESULTS
      const historicalAppts = appointmentsResponse.appointments.filter(
        (appointment) => 
          appointment.location === LocationType.HISTORICRESULTS
      );

      // Transform to SelectOption format
      const appointmentOptions: SelectOption[] = historicalAppts.map((appointment) => ({
        key: appointment.id,
        value: `${appointment.title} (ID: ${appointment.id})`
      }));

      setHistoricalAppointments(appointmentOptions);
    } catch (error) {
      console.error('Error fetching historical appointments:', error);
      setSaveError('Failed to load appointments');
    } finally {
      setIsLoadingAppointments(false);
    }
  }, [userId]);

  // Fetch historical result appointments and biomarkers list when modal opens
  React.useEffect(() => {
    if (isOpen && userId) {
      fetchHistoricalAppointments();
      fetchBiomarkersList();
    } else {
      // Reset state when modal closes
      setSelectedAppointment(null);
      setSaveError(null);
      setAppointmentError(null);
      setSelectedExtractionDate(null);
      setInfoMessage(null);
    }
  }, [isOpen, userId, fetchHistoricalAppointments, fetchBiomarkersList]);

  const handleBiomarkersUpdate = (updated: any[]) => {
    setUpdatedBiomarkers(updated);
    // Update the responseData with the new matched_biomarkers
    if (responseData) {
      (responseData as any).matched_biomarkers = updated;
    }
  };

  const findBiomarkerByName = async (name: string, biomarkersListToSearch: any[]): Promise<any> => {
    if (!name || biomarkersListToSearch.length === 0) {
      console.warn('findBiomarkerByName: No name or biomarkers list is empty', { name, listLength: biomarkersListToSearch.length });
      return null;
    }
    
    const normalizedName = name.trim().toLowerCase();
    console.log(`Searching for biomarker: "${name}" (normalized: "${normalizedName}")`);
    console.log(`Total biomarkers in list: ${biomarkersListToSearch.length}`);
    
    // Try exact match first (case-insensitive, trimmed)
    let found = biomarkersListToSearch.find(
      (b: any) => b.name?.trim().toLowerCase() === normalizedName
    );
    
    if (found) {
      console.log(`✓ Found biomarker by exact match: "${name}" -> "${found.name}" (ID: ${found.id})`);
      return found;
    }
    
    // Try case-insensitive match (without trim check)
    found = biomarkersListToSearch.find(
      (b: any) => b.name?.toLowerCase() === normalizedName
    );
    
    if (found) {
      console.log(`✓ Found biomarker by case-insensitive match: "${name}" -> "${found.name}" (ID: ${found.id})`);
      return found;
    }
    
    // Try lab_bm_id match
    found = biomarkersListToSearch.find(
      (b: any) => b.lab_bm_id?.toLowerCase() === normalizedName
    );
    
    if (found) {
      console.log(`✓ Found biomarker by lab_bm_id match: "${name}" -> "${found.name}" (ID: ${found.id})`);
      return found;
    }
    
    // Try removing common suffixes/prefixes and matching
    const nameWithoutCommonWords = normalizedName
      .replace(/\b(count|level|value|result|test)\b/gi, '')
      .trim();
    
    if (nameWithoutCommonWords && nameWithoutCommonWords !== normalizedName) {
      found = biomarkersListToSearch.find(
        (b: any) => {
          const biomarkerName = b.name?.toLowerCase() || '';
          const biomarkerNameClean = biomarkerName
            .replace(/\b(count|level|value|result|test)\b/gi, '')
            .trim();
          return biomarkerNameClean === nameWithoutCommonWords || biomarkerName === nameWithoutCommonWords;
        }
      );
      
      if (found) {
        console.log(`✓ Found biomarker by cleaned name match: "${name}" -> "${found.name}" (ID: ${found.id})`);
        return found;
      }
    }
    
    // Try partial match (contains) - more flexible
    found = biomarkersListToSearch.find(
      (b: any) => {
        const biomarkerName = b.name?.toLowerCase() || '';
        return biomarkerName.includes(normalizedName) || normalizedName.includes(biomarkerName);
      }
    );
    
    if (found) {
      console.log(`✓ Found biomarker by partial match: "${name}" -> "${found.name}" (ID: ${found.id})`);
      return found;
    }
    
    // Try fuzzy match - match all significant words
    const searchWords = normalizedName.split(/\s+/).filter(w => w.length > 2);
    if (searchWords.length > 0) {
      found = biomarkersListToSearch.find(
        (b: any) => {
          const biomarkerName = b.name?.toLowerCase() || '';
          return searchWords.every(word => biomarkerName.includes(word));
        }
      );
      
      if (found) {
        console.log(`✓ Found biomarker by fuzzy match: "${name}" -> "${found.name}" (ID: ${found.id})`);
        return found;
      }
    }
    
    // Try API search as fallback
    console.log(`Trying API search for: "${name}"`);
    try {
      const searchResponse = await getBiomarkersList(name);
      const searchResults = searchResponse.biomarkers || [];
      
      if (searchResults.length > 0) {
        // Try to find exact match in search results
        found = searchResults.find(
          (b: any) => b.name?.trim().toLowerCase() === normalizedName
        );
        
        if (found) {
          console.log(`✓ Found biomarker via API search: "${name}" -> "${found.name}" (ID: ${found.id})`);
          return found;
        }
        
        // Try case-insensitive match in search results
        found = searchResults.find(
          (b: any) => b.name?.toLowerCase() === normalizedName
        );
        
        if (found) {
          console.log(`✓ Found biomarker via API search (case-insensitive): "${name}" -> "${found.name}" (ID: ${found.id})`);
          return found;
        }
        
        // Return first result if any found (might be close match)
        if (searchResults.length > 0) {
          console.log(`⚠ Using first API search result: "${name}" -> "${searchResults[0].name}" (ID: ${searchResults[0].id})`);
          return searchResults[0];
        }
      }
    } catch (error) {
      console.warn('API search failed:', error);
    }
    
    // Log similar biomarkers for debugging
    const similarBiomarkers = biomarkersListToSearch
      .filter((b: any) => {
        const biomarkerName = b.name?.toLowerCase() || '';
        return biomarkerName.includes(normalizedName.substring(0, 5)) || 
               normalizedName.includes(biomarkerName.substring(0, 5));
      })
      .slice(0, 5)
      .map((b: any) => b.name);
    
    console.warn(`✗ Biomarker not found: "${name}"`);
    if (similarBiomarkers.length > 0) {
      console.warn(`Similar biomarkers found:`, similarBiomarkers);
    }
    
    return null;
  };

  const handleSave = async () => {
    setInfoMessage(null);
    if (!selectedAppointment || !userId) {
      setAppointmentError('Please select an appointment');
      setSaveError(null);
      return;
    }
    
    // Clear appointment error if appointment is selected
    setAppointmentError(null);

    if (updatedBiomarkers.length === 0) {
      setSaveError('No biomarkers to save');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Ensure biomarkers list is loaded - fetch fresh if empty
      let currentBiomarkersList = biomarkersList;
      if (currentBiomarkersList.length === 0) {
        console.log('Biomarkers list is empty, fetching...');
        try {
          const response = await getBiomarkersList();
          currentBiomarkersList = response.biomarkers || [];
          setBiomarkersList(currentBiomarkersList);
          if (currentBiomarkersList.length === 0) {
            throw new Error('Failed to load biomarkers list. Please try again.');
          }
        } catch (error) {
          console.error('Error fetching biomarkers list:', error);
          throw new Error('Failed to load biomarkers list. Please try again.');
        }
      }

      console.log(`Processing ${updatedBiomarkers.length} biomarkers for save`);
      console.log('Sample biomarker structure:', updatedBiomarkers[0]);

      // Transform biomarkers to the format required by the API
      const skippedBiomarkers: string[] = [];
      const biomarkersForApi = await Promise.all(
        updatedBiomarkers.map(async (biomarker: any, index: number) => {
          console.log(`Processing biomarker ${index + 1}:`, biomarker);
          // Extract biomarker_id from the biomarker data
          // The biomarker might have 'Biomarker Name' with an object containing id, or 'biomarker_id'
          let biomarkerId: number | undefined;
          let biomarkerName: string | undefined;
          
          // Try different ways to get the biomarker ID
          if (biomarker.biomarker_id) {
            biomarkerId = biomarker.biomarker_id;
          } else if (biomarker['Biomarker Name']) {
            if (typeof biomarker['Biomarker Name'] === 'object' && biomarker['Biomarker Name'].id) {
              biomarkerId = biomarker['Biomarker Name'].id;
            } else if (typeof biomarker['Biomarker Name'] === 'string') {
              biomarkerName = biomarker['Biomarker Name'];
            }
          } else if (biomarker.biomarker_name) {
            biomarkerName = biomarker.biomarker_name;
          }

          // If we don't have an ID but have a name, try to look it up using improved matching
          if (!biomarkerId && biomarkerName) {
            const foundBiomarker = await findBiomarkerByName(biomarkerName, currentBiomarkersList);
            if (foundBiomarker?.id) {
              biomarkerId = foundBiomarker.id;
            }
          }

          // If still no ID, try to extract name from other possible fields
          if (!biomarkerId && !biomarkerName) {
            // Try to get name from any string field that might contain the biomarker name
            const possibleNameFields = ['Biomarker Name', 'biomarker_name', 'name', 'Report Name'];
            for (const field of possibleNameFields) {
              if (biomarker[field] && typeof biomarker[field] === 'string') {
                const extractedName = biomarker[field] as string;
                biomarkerName = extractedName;
                const foundBiomarker = await findBiomarkerByName(extractedName, currentBiomarkersList);
                if (foundBiomarker?.id) {
                  biomarkerId = foundBiomarker.id;
                  break;
                }
              }
            }
          }

          // If biomarker ID is still not found, skip it instead of throwing error
          if (!biomarkerId) {
            const name = biomarkerName || biomarker['Biomarker Name'] || biomarker.biomarker_name || 'Unknown';
            console.warn(`⚠ Skipping biomarker "${name}" - not found in system`);
            skippedBiomarkers.push(name);
            return null; // Return null to indicate this biomarker should be skipped
          }

          return {
            biomarker_id: biomarkerId,
            value: String(biomarker.value || biomarker['Biomarker Value'] || ''),
            summary: biomarker.summary || biomarker['Summary'] || '',
            unit: biomarker.unit || biomarker['Biomarker Unit'] || biomarker['Report Unit'] || ''
          };
        })
      );

      // Filter out null values (skipped biomarkers) and biomarkers without valid IDs
      const validBiomarkers = biomarkersForApi.filter((b): b is NonNullable<typeof b> => b !== null && !!b.biomarker_id);

      if (validBiomarkers.length === 0) {
        throw new Error('No valid biomarkers found to save. All biomarkers were either not found in the system or invalid.');
      }

      // Show warning if some biomarkers were skipped
      if (skippedBiomarkers.length > 0) {
        const skippedMessage = `Skipped ${skippedBiomarkers.length} biomarker(s) not found in system: ${skippedBiomarkers.join(', ')}`;
        console.warn(skippedMessage);
        // Log the skipped biomarkers for user reference
        console.log(`The following biomarkers were skipped:`, skippedBiomarkers);
      }

      const requestData = {
        appointment_id: selectedAppointment.key,
        member_id: userId,
        biomarkers: validBiomarkers
      };

      await addTestResults(requestData);

      const successMessage = skippedBiomarkers.length > 0
        ? `Successfully saved ${validBiomarkers.length} biomarker(s). ${skippedBiomarkers.length} biomarker(s) were skipped (not found in system).`
        : `Successfully saved ${validBiomarkers.length} biomarker(s).`;
      console.log(successMessage);
      
      setSaveError(null);

      // Multi-date flow: keep popup open and ask user to assign another date.
      if (showMultiDatePicker) {
        setInfoMessage(
          `${successMessage} Assign another appointment for another extracted date.`
        );
        setAppointmentError(null);
        setSelectedAppointment(null);
        // Force user to re-select date to continue (per requirement).
        setSelectedExtractionDate(null);
        setUpdatedBiomarkers([]);
        return;
      }

      // Single-date flow: keep existing behavior (save -> close).
      if (onSaveSuccess) onSaveSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving biomarkers:', error);
      setSaveError(error.message || 'Failed to save biomarkers');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <style>{`
        .overlay .modalWrapper.biomarker-extraction-modal {
          width: 90vw !important;
          max-width: 90vw !important;
          max-height: 90vh !important;
        }
        .overlay .modalWrapper.biomarker-extraction-modal .modal {
          width: 90vw !important;
          max-width: 90vw !important;
          height: 90vh !important;
          max-height: 90vh !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          display: flex !important;
          flex-direction: column !important;
        }
        .overlay .modalWrapper.biomarker-extraction-modal .modal .content {
          flex: 1 !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          display: flex !important;
          flex-direction: column !important;
          min-height: 0 !important;
        }
      `}</style>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Extracted Report Data"
        memberName={memberName}
        width={'90vw'}
      >
        <div className="extracted-data-modal-content">
          {showMultiDatePicker && (
            <div className="extraction-date-picker-section">
              <Select
                name="extraction_date"
                title="Select Extraction Date"
                options={dateOptions}
                selectedOption={
                  selectedExtractionDate
                    ? { key: selectedExtractionDate, value: selectedExtractionDate }
                    : { key: '', value: '' }
                }
                onChange={(option) => {
                  const nextDate = option?.value ?? null;
                  setSelectedExtractionDate(nextDate || null);
                  setSelectedAppointment(null);
                  setAppointmentError(null);
                  setSaveError(null);
                  setInfoMessage(null);

                  const bucket = extractionDateBuckets.find((b) => b.date === nextDate);
                  setUpdatedBiomarkers(bucket?.biomarkers ?? []);
                }}
                placeholder="Select an extraction date"
                isLoading={false}
                preventAutoOpenOnLoad={true}
                isError={false}
              />
            </div>
          )}

          {infoMessage && <div className="extraction-info-message">{infoMessage}</div>}

          <ExtractedDataTable
            key={showMultiDatePicker ? selectedExtractionDate ?? 'none' : 'single'}
            biomarkers={updatedBiomarkers}
            summary={summary}
            responseData={responseData}
            missingList={missingList}
            onBiomarkersUpdate={handleBiomarkersUpdate}
          />
          <div>
          {/* Appointment Selection Section */}
          {updatedBiomarkers.length > 0 && (
            <div className="appointment-selection-section">
              <div className="appointment-select-container">
                <Select
                  name="appointment"
                  title="Select Historical Results Appointment"
                  options={historicalAppointments}
                  selectedOption={selectedAppointment || { key: '', value: '' }}
                  onChange={(option) => {
                    setSelectedAppointment(option);
                    setAppointmentError(null); // Clear error when appointment is selected
                  }}
                  placeholder={isLoadingAppointments ? "Loading appointments..." : "Select an appointment"}
                  isLoading={isLoadingAppointments}
                  preventAutoOpenOnLoad={true}
                  isError={!!appointmentError}
                  errorMessage={appointmentError || undefined}
                />
              </div>
              {saveError && selectedAppointment && (
                <div className="save-error-message">{saveError}</div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <Button
              type="button"
              onClick={onClose}
              text="Close"
              isSecondary={false}
              backgroundColor='rgb(235, 239, 243)'
              color='#000'
            />
            {updatedBiomarkers.length > 0 && (
              <Button
                type="button"
                onClick={handleSave}
                text={isSaving ? "Saving..." : "Save"}
                isSecondary={false}
                isDisabled={!selectedAppointment || isSaving}
              />
            )}
          </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BiomarkerExtractionModal;

